import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Tooltip,
    useTheme,
    useMediaQuery,
    Paper,
    Fade,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useTranslation } from 'react-i18next';
import { getFormattedMarketData } from '../services/marketDataService';

interface MarketDataTickerProps {
    isRTL?: boolean;
}

const MarketDataTicker: React.FC<MarketDataTickerProps> = ({ isRTL = false }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [marketData, setMarketData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const fetchMarketData = async () => {
            try {
                setLoading(true);
                setError(false);
                const data = await getFormattedMarketData();
                setMarketData(data);
            } catch (err) {
                console.error('Error fetching market data:', err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchMarketData();

        // Refresh every 2 minutes
        const interval = setInterval(fetchMarketData, 2 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    py: 1,
                    width: '100%'
                }}
            >
                <CircularProgress size={16} />
                <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    {t('market.loading')}
                </Typography>
            </Box>
        );
    }

    if (error || !marketData) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 1,
                    width: '100%'
                }}
            >
                <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem' }}>
                    {t('market.error')}
                </Typography>
            </Box>
        );
    }

    const MarketChip: React.FC<{
        symbol: string;
        name: string;
        price: string;
        change: string;
        changePercent: string;
        isPositive: boolean;
    }> = ({ symbol, name, price, change, changePercent, isPositive }) => (
        <Tooltip
            title={`${name}: ${price} (${change} ${changePercent})`}
            arrow
            placement="top"
        >
            <Paper
                elevation={1}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 2,
                    backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'white',
                    border: `1px solid ${theme.palette.divider}`,
                    minWidth: 'fit-content',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: theme.shadows[4],
                    },
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                        color: 'primary.main',
                        minWidth: 'fit-content'
                    }}
                >
                    {symbol}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: '0.65rem',
                        fontWeight: 'medium',
                        minWidth: 'fit-content'
                    }}
                >
                    {price}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    {isPositive ? (
                        <TrendingUpIcon sx={{ fontSize: '0.7rem', color: 'success.main' }} />
                    ) : (
                        <TrendingDownIcon sx={{ fontSize: '0.7rem', color: 'error.main' }} />
                    )}
                    <Typography
                        variant="caption"
                        sx={{
                            fontSize: '0.65rem',
                            color: isPositive ? 'success.main' : 'error.main',
                            fontWeight: 'bold',
                            minWidth: 'fit-content'
                        }}
                    >
                        {changePercent}
                    </Typography>
                </Box>
            </Paper>
        </Tooltip>
    );

    return (
        <Fade in={true} timeout={500}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    width: '100%',
                    direction: isRTL ? 'rtl' : 'ltr',
                    overflow: 'hidden',
                    px: 1,
                }}
            >
                {!isMobile && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            backgroundColor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200',
                        }}
                    >
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                            📈
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.8 }}>
                            {t('market.lastUpdated')}
                        </Typography>
                    </Box>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexWrap: 'nowrap',
                        overflow: 'auto',
                        width: '100%',
                        maxWidth: isMobile ? '100%' : '800px',
                        '&::-webkit-scrollbar': {
                            height: 4,
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: theme.palette.divider,
                            borderRadius: 2,
                        },
                    }}
                >
                    <MarketChip
                        symbol="SPX"
                        name={t('market.sp500')}
                        price={marketData.sp500.formattedPrice}
                        change={marketData.sp500.formattedChange}
                        changePercent={marketData.sp500.formattedChangePercent}
                        isPositive={marketData.sp500.isPositive}
                    />

                    <MarketChip
                        symbol="IXIC"
                        name={t('market.nasdaq')}
                        price={marketData.nasdaq.formattedPrice}
                        change={marketData.nasdaq.formattedChange}
                        changePercent={marketData.nasdaq.formattedChangePercent}
                        isPositive={marketData.nasdaq.isPositive}
                    />

                    <MarketChip
                        symbol="XAU"
                        name={t('market.gold')}
                        price={marketData.gold.formattedPrice}
                        change={marketData.gold.formattedChange}
                        changePercent={marketData.gold.formattedChangePercent}
                        isPositive={marketData.gold.isPositive}
                    />

                    <MarketChip
                        symbol="BTC"
                        name={t('market.bitcoin')}
                        price={marketData.bitcoin.formattedPrice}
                        change={marketData.bitcoin.formattedChange}
                        changePercent={marketData.bitcoin.formattedChangePercent}
                        isPositive={marketData.bitcoin.isPositive}
                    />

                    <MarketChip
                        symbol="ETH"
                        name={t('market.ethereum')}
                        price={marketData.ethereum.formattedPrice}
                        change={marketData.ethereum.formattedChange}
                        changePercent={marketData.ethereum.formattedChangePercent}
                        isPositive={marketData.ethereum.isPositive}
                    />
                </Box>
            </Box>
        </Fade>
    );
};

export default MarketDataTicker;
